
import { Component, Vue, Watch } from 'vue-property-decorator';
import { useLiaisonStore } from '../../model';
import { TransitionAction } from '../../model/types';
import type { HistoryItem, NotificationType } from '../../model/types';
import { StatusPanel } from '../statusPanel';
import { StatusReportSection } from '../statusReportSection';
import { DispatchRequestSection } from '../dispatchRequestSection';
import { StandbyReleaseSection } from '../standbyReleaseSection';
import { EmergencyTimeInput } from '../emergencyTimeInput';
import { SendHistory } from '../sendHistory';
import { ConfirmDialog } from '../confirmDialog';

const POLLING_INTERVAL_MS = 10 * 60 * 1000;

type PendingAction =
  | { kind: 'send'; type: NotificationType; subject?: string; body: string }
  | { kind: 'transition'; action: typeof TransitionAction.ManualOn | typeof TransitionAction.ManualOff }
  | { kind: 'history' }
  | null;

@Component({
  components: {
    StatusPanel,
    StatusReportSection,
    DispatchRequestSection,
    StandbyReleaseSection,
    EmergencyTimeInput,
    SendHistory,
    ConfirmDialog,
  },
})
export default class LiaisonNotification extends Vue {
  store = useLiaisonStore();

  pollingId: number | null = null;

  dialogOpen = false;

  dialogTitle = '';

  dialogBody = '';

  dialogConfirmLabel = '送信';

  dialogTransitionLabel = '';

  dialogEditable = false;

  dialogReadOnly = false;

  pendingAction: PendingAction = null;

  get basinId(): string {
    return this.store.selectedTarget?.basinId ?? '';
  }

  get stationId(): string {
    return this.store.selectedTarget?.stationId ?? '';
  }

  get target() {
    return { basinId: this.basinId, stationId: this.stationId };
  }

  get selectedTargetKey(): string {
    if (!this.store.selectedTarget) return '';
    return `${this.store.selectedTarget.basinId}/${this.store.selectedTarget.stationId}`;
  }

  get targetItems() {
    return this.store.targets.map((target) => ({
      text: target.damName,
      value: `${target.basinId}/${target.stationId}`,
    }));
  }

  onSelectTarget(key: string) {
    const target = this.store.targets.find(
      (it) => `${it.basinId}/${it.stationId}` === key,
    );
    if (target) {
      this.store.selectTarget(target);
    }
  }

  get dispatchTemplate() {
    return this.store.templates.dispatchRequest;
  }

  get standbyTemplate() {
    return this.store.templates.standbyRelease;
  }

  get statusBodyOnly(): string {
    // 現況報告プレビューから【現況報告】の見出し行を除いた本文部分
    const body = this.store.statusReportPreview;
    if (!body) return '';
    const lines = body.split('\n');
    if (lines[0]?.includes('【現況報告】')) {
      return lines.slice(1).join('\n');
    }
    return body;
  }

  async created() {
    await this.store.fetchTargets();
    await this.refresh();
    this.pollingId = window.setInterval(() => this.refresh(), POLLING_INTERVAL_MS);
  }

  @Watch('selectedTargetKey')
  async onTargetChange() {
    await this.refresh();
  }

  beforeDestroy() {
    if (this.pollingId !== null) {
      window.clearInterval(this.pollingId);
      this.pollingId = null;
    }
  }

  async refresh() {
    if (!this.basinId || !this.stationId) return;
    await Promise.all([
      this.store.fetchState(this.target),
      this.store.fetchHistory(this.target),
      this.store.fetchAllTemplates({ basinId: this.basinId }),
      this.store.fetchPreview(this.target),
    ]);
  }

  onOpenStatusReportConfirm() {
    this.dialogTitle = '現況報告 - 送信確認';
    this.dialogBody = this.store.statusReportPreview;
    this.dialogConfirmLabel = '送信';
    this.dialogTransitionLabel = '';
    this.dialogEditable = false;
    this.dialogReadOnly = false;
    this.pendingAction = {
      kind: 'send',
      type: 'statusReport',
      body: this.store.statusReportPreview,
    };
    this.dialogOpen = true;
  }

  onSendDispatch(payload: { subject: string; body: string }) {
    this.dialogTitle = '派遣要請 - 送信確認';
    this.dialogBody = payload.body;
    this.dialogConfirmLabel = '送信';
    this.dialogTransitionLabel = this.store.isSending
      ? ''
      : '送信中（自動送信開始）';
    this.dialogEditable = true;
    this.dialogReadOnly = false;
    this.pendingAction = {
      kind: 'send',
      type: 'dispatchRequest',
      subject: payload.subject,
      body: payload.body,
    };
    this.dialogOpen = true;
  }

  onSendStandby(payload: { subject: string; body: string }) {
    this.dialogTitle = '待機解除 - 送信確認';
    this.dialogBody = payload.body;
    this.dialogConfirmLabel = '送信';
    this.dialogTransitionLabel = '';
    this.dialogEditable = true;
    this.dialogReadOnly = false;
    this.pendingAction = {
      kind: 'send',
      type: 'standbyRelease',
      subject: payload.subject,
      body: payload.body,
    };
    this.dialogOpen = true;
  }

  onAutoSendStart() {
    this.dialogTitle = '自動送信開始の確認';
    this.dialogBody = '現況報告の自動送信を開始します。';
    this.dialogConfirmLabel = '開始';
    this.dialogTransitionLabel = '送信中';
    this.dialogEditable = false;
    this.dialogReadOnly = false;
    this.pendingAction = { kind: 'transition', action: TransitionAction.ManualOn };
    this.dialogOpen = true;
  }

  onAutoSendStop() {
    this.dialogTitle = '自動送信停止の確認';
    this.dialogBody = '現況報告の自動送信を停止します。';
    this.dialogConfirmLabel = '停止';
    this.dialogTransitionLabel = '停止中';
    this.dialogEditable = false;
    this.dialogReadOnly = false;
    this.pendingAction = { kind: 'transition', action: TransitionAction.ManualOff };
    this.dialogOpen = true;
  }

  async onEmergencyTimeSubmit(yyyymmddhhmm: string) {
    const action = this.store.isEmergencyTimeSet
      ? TransitionAction.ResetEmergencyTime
      : TransitionAction.SetEmergencyTime;
    await this.store.updateState(this.target, {
      action,
      emergencyReleaseTime: yyyymmddhhmm,
    });
  }

  async onEmergencyTimeClear() {
    await this.store.updateState(this.target, {
      action: TransitionAction.UnsetEmergencyTime,
    });
  }

  async onSaveDispatchTemplate(payload: { subject: string; body: string }) {
    await this.store.updateTemplate(
      { basinId: this.basinId },
      'dispatchRequest',
      payload,
    );
  }

  async onSaveStandbyTemplate(payload: { subject: string; body: string }) {
    await this.store.updateTemplate(
      { basinId: this.basinId },
      'standbyRelease',
      payload,
    );
  }

  async onDialogConfirm() {
    const action = this.pendingAction;
    if (!action) {
      this.dialogOpen = false;
      return;
    }

    try {
      if (action.kind === 'send') {
        await this.store.send(this.target, {
          type: action.type,
          subject: action.subject,
          body: this.dialogBody,
        });
        await this.store.fetchHistory(this.target);
      } else if (action.kind === 'transition') {
        await this.store.updateState(this.target, { action: action.action });
      }
    } catch (error) {
      console.error('[liaison] dialog confirm failed', error);
      window.alert('操作に失敗しました。時間をおいて再度お試しください。');
      return;
    } finally {
      this.pendingAction = null;
      this.dialogOpen = false;
    }
  }

  onHistorySelect(item: HistoryItem) {
    this.dialogTitle = `送信履歴 - ${this.formatDate(item.sentAt)}`;
    this.dialogBody = item.body;
    this.dialogConfirmLabel = '閉じる';
    this.dialogTransitionLabel = '';
    this.dialogEditable = false;
    this.dialogReadOnly = true;
    this.pendingAction = { kind: 'history' };
    this.dialogOpen = true;
  }

  formatDate(iso: string): string {
    const sentAt = new Date(iso);
    return sentAt.toLocaleString('ja-JP');
  }
}
